import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Container,
  Loader,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import type {
  PractitionerFormField,
  PractitionerFormMode,
} from '../../../../../shared/types/firestore/practitionerForm';
import { computeVisiblePractitionerFieldIds } from '../../../../../shared/forms/practitionerFormVisibility';
import { invokeBackendRoute } from '@/backend/invoke';

type LoadSuccess = {
  status: 'success';
  payload: {
    formId: string;
    firmId: string;
    title: string;
    version: number;
    mode: PractitionerFormMode;
    fields: PractitionerFormField[];
  };
};

type ContractError = { status: 'error'; userMessage: string };

type SubmitSuccess = {
  status: 'success';
  payload: {
    submissionId: string;
    matterId: string;
    matterTitle: string;
  };
};

const isContractError = (value: unknown): value is ContractError =>
  typeof value === 'object' &&
  value !== null &&
  'status' in value &&
  (value as ContractError).status === 'error';

const scaleBounds = (field: PractitionerFormField) => ({
  min: field.scaleMin ?? 1,
  max: field.scaleMax ?? 5,
});

export const IntakeFormPage = () => {
  const { firmId = '', formId = '' } = useParams<{
    firmId: string;
    formId: string;
  }>();
  const [searchParams] = useSearchParams();
  const matterIdFromLink = searchParams.get('matterId')?.trim() || undefined;

  const [loadState, setLoadState] = useState<
    'idle' | 'loading' | 'ready' | 'notFound' | 'error'
  >('idle');
  const [formTitle, setFormTitle] = useState('');
  const [formMode, setFormMode] = useState<PractitionerFormMode | null>(null);
  const [fields, setFields] = useState<PractitionerFormField[]>([]);
  const [loadErrorMessage, setLoadErrorMessage] = useState('');

  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState<
    SubmitSuccess['payload'] | null
  >(null);

  useEffect(() => {
    if (!firmId || !formId) {
      setLoadState('notFound');
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoadState('loading');
      setLoadErrorMessage('');
      try {
        const response = await invokeBackendRoute<
          LoadSuccess | ContractError,
          { firmId: string; formId: string; matterId?: string }
        >('getPublicIntakeFormDefinition', {
          firmId,
          formId,
          ...(matterIdFromLink ? { matterId: matterIdFromLink } : {}),
        });
        if (cancelled) {
          return;
        }
        if (isContractError(response)) {
          setLoadState('notFound');
          setLoadErrorMessage(response.userMessage);
          return;
        }
        setFormTitle(response.payload.title);
        setFormMode(response.payload.mode);
        setFields(response.payload.fields);
        const initial: Record<string, string | string[]> = {};
        for (const field of response.payload.fields) {
          initial[field.id] = field.type === 'checkboxes' ? [] : '';
        }
        setAnswers(initial);
        setLoadState('ready');
      } catch (error) {
        if (cancelled) {
          return;
        }
        setLoadState('error');
        setLoadErrorMessage(
          error instanceof Error ? error.message : 'Something went wrong',
        );
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [firmId, formId, matterIdFromLink]);

  useEffect(() => {
    if (formTitle) {
      document.title = `${formTitle} · Practist`;
    }
    return () => {
      document.title = 'Practist';
    };
  }, [formTitle]);

  const setStringAnswer = useCallback((fieldId: string, value: string) => {
    setAnswers((previous) => ({ ...previous, [fieldId]: value }));
  }, []);

  const setCheckboxAnswer = useCallback((fieldId: string, value: string[]) => {
    setAnswers((previous) => ({ ...previous, [fieldId]: value }));
  }, []);

  useEffect(() => {
    if (loadState !== 'ready' || fields.length === 0) {
      return;
    }
    const visible = computeVisiblePractitionerFieldIds({
      fields,
      rawAnswers: answers as Record<string, unknown>,
    });
    setAnswers((previous) => {
      let changed = false;
      const next = { ...previous };
      for (const f of fields) {
        if (visible.has(f.id)) {
          continue;
        }
        const empty = f.type === 'checkboxes' ? [] : '';
        const cur = previous[f.id];
        const isEmpty =
          f.type === 'checkboxes'
            ? Array.isArray(cur) && cur.length === 0
            : typeof cur === 'string' && cur.length === 0;
        if (!isEmpty) {
          next[f.id] = empty;
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, [fields, answers, loadState]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!firmId || !formId || loadState !== 'ready') {
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const visibleIds = computeVisiblePractitionerFieldIds({
        fields,
        rawAnswers: answers as Record<string, unknown>,
      });
      const submissionAnswers: Record<string, unknown> = {};
      for (const id of visibleIds) {
        submissionAnswers[id] = answers[id];
      }
      const response = await invokeBackendRoute<
        SubmitSuccess | ContractError,
        {
          firmId: string;
          formId: string;
          answers: Record<string, unknown>;
          matterId?: string;
        }
      >('submitPublicIntakeFormSubmission', {
        firmId,
        formId,
        answers: submissionAnswers,
        ...(matterIdFromLink ? { matterId: matterIdFromLink } : {}),
      });
      if (isContractError(response)) {
        setSubmitError(response.userMessage);
        return;
      }
      setSubmitSuccess(response.payload);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'Something went wrong',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const visibleFields = useMemo(() => {
    const visibleIds = computeVisiblePractitionerFieldIds({
      fields,
      rawAnswers: answers as Record<string, unknown>,
    });
    return fields.filter((field) => visibleIds.has(field.id));
  }, [fields, answers]);

  const fieldNodes = useMemo(() => {
    return visibleFields.map((field) => {
      const value = answers[field.id];

      switch (field.type) {
        case 'short_text':
        case 'first_name':
        case 'last_name':
        case 'phone':
          return (
            <TextInput
              key={field.id}
              label={field.label}
              placeholder={field.placeholder}
              required={field.required}
              value={typeof value === 'string' ? value : ''}
              onChange={(e) => setStringAnswer(field.id, e.target.value)}
            />
          );

        case 'long_text':
          return (
            <Textarea
              key={field.id}
              label={field.label}
              placeholder={field.placeholder}
              required={field.required}
              autosize
              minRows={3}
              value={typeof value === 'string' ? value : ''}
              onChange={(e) => setStringAnswer(field.id, e.target.value)}
            />
          );

        case 'email':
          return (
            <TextInput
              key={field.id}
              type='email'
              label={field.label}
              placeholder={field.placeholder}
              required={field.required}
              value={typeof value === 'string' ? value : ''}
              onChange={(e) => setStringAnswer(field.id, e.target.value)}
            />
          );

        case 'dropdown': {
          const opts = (field.options ?? []).map((o: string) => ({
            value: o,
            label: o,
          }));
          return (
            <Select
              key={field.id}
              label={field.label}
              placeholder={field.placeholder ?? 'Choose one'}
              required={field.required}
              data={opts}
              clearable={!field.required}
              value={
                typeof value === 'string' && value.length > 0 ? value : null
              }
              onChange={(v) => setStringAnswer(field.id, v ?? '')}
            />
          );
        }

        case 'multiple_choice':
          return (
            <Radio.Group
              key={field.id}
              label={field.label}
              required={field.required}
              value={typeof value === 'string' ? value : ''}
              onChange={(v) => setStringAnswer(field.id, v)}
            >
              <Stack gap='xs' mt={4}>
                {(field.options ?? []).map((option: string) => (
                  <Radio key={option} value={option} label={option} />
                ))}
              </Stack>
            </Radio.Group>
          );

        case 'checkboxes':
          return (
            <Checkbox.Group
              key={field.id}
              label={field.label}
              required={field.required}
              value={Array.isArray(value) ? value : []}
              onChange={(v) => setCheckboxAnswer(field.id, v)}
            >
              <Stack gap='xs' mt={4}>
                {(field.options ?? []).map((option: string) => (
                  <Checkbox key={option} value={option} label={option} />
                ))}
              </Stack>
            </Checkbox.Group>
          );

        case 'linear_scale': {
          const { min, max } = scaleBounds(field);
          const numericOptions = Array.from(
            { length: max - min + 1 },
            (_, index) => min + index,
          );
          return (
            <Stack key={field.id} gap={6}>
              <Text size='sm' fw={500}>
                {field.label}
                {field.required ? ' *' : ''}
              </Text>
              {(field.scaleMinLabel || field.scaleMaxLabel) && (
                <Text size='xs' c='dimmed'>
                  {field.scaleMinLabel ?? min} → {field.scaleMaxLabel ?? max}
                </Text>
              )}
              <Radio.Group
                value={typeof value === 'string' ? value : ''}
                onChange={(v) => setStringAnswer(field.id, v)}
              >
                <Stack gap='xs' mt={4}>
                  {numericOptions.map((n) => (
                    <Radio key={n} value={String(n)} label={String(n)} />
                  ))}
                </Stack>
              </Radio.Group>
            </Stack>
          );
        }

        case 'date':
          return (
            <TextInput
              key={field.id}
              type='date'
              label={field.label}
              required={field.required}
              value={typeof value === 'string' ? value : ''}
              onChange={(e) => setStringAnswer(field.id, e.target.value)}
            />
          );

        case 'time':
          return (
            <TextInput
              key={field.id}
              type='time'
              label={field.label}
              description='24-hour time (HH:MM)'
              required={field.required}
              value={typeof value === 'string' ? value : ''}
              onChange={(e) => setStringAnswer(field.id, e.target.value)}
            />
          );

        default: {
          const _exhaustive: never = field.type;
          return (
            <Text key={field.id} c='red' size='sm'>
              Unsupported field type: {_exhaustive}
            </Text>
          );
        }
      }
    });
  }, [answers, visibleFields, setStringAnswer, setCheckboxAnswer]);

  if (loadState === 'loading' || loadState === 'idle') {
    return (
      <Container size='sm' py='xl'>
        <Stack align='center' gap='md'>
          <Loader type='dots' color='pink.4' size='lg' />
          <Text size='sm' c='dimmed'>
            Loading form…
          </Text>
        </Stack>
      </Container>
    );
  }

  if (loadState === 'notFound' || loadState === 'error') {
    const fallbackMessage =
      loadState === 'error'
        ? 'Something went wrong while loading this form.'
        : 'This form is not available. It may have been removed or the link may be incorrect.';
    return (
      <Container size='sm' py='xl'>
        <Title order={2}>Form unavailable</Title>
        <Text c='dimmed' mt='sm'>
          {loadErrorMessage.length > 0 ? loadErrorMessage : fallbackMessage}
        </Text>
      </Container>
    );
  }

  if (submitSuccess) {
    const isQuestionnaire = formMode === 'questionnaire_existing_matter';
    return (
      <Container size='sm' py='xl'>
        <Title order={2}>Thank you</Title>
        <Text mt='md'>
          {isQuestionnaire
            ? 'Your responses were saved successfully.'
            : 'Your responses were submitted successfully.'}
        </Text>
        <Text mt='sm' size='sm' c='dimmed'>
          {isQuestionnaire ? 'Matter: ' : 'New matter title: '}
          {submitSuccess.matterTitle}
        </Text>
      </Container>
    );
  }

  return (
    <Container size='sm' py='xl'>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap='lg'>
          <div>
            <Title order={1}>{formTitle}</Title>
            <Text size='sm' c='dimmed' mt={6}>
              Fields marked with * are required.
            </Text>
          </div>
          {fieldNodes}
          {submitError ? (
            <Text size='sm' c='red'>
              {submitError}
            </Text>
          ) : null}
          <Button
            type='submit'
            loading={submitting}
            color='practistBlue'
            size='md'
          >
            Submit
          </Button>
        </Stack>
      </form>
    </Container>
  );
};
